import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../services/api';
import BrandPanel from '../components/BrandPanel';
import Logo from '../components/Logo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Button, Input, Select, Field } from '../components/ui';

function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    role: 'student',
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();

  // Backend field names (snake_case) -> the form state keys used here.
  const BACKEND_FIELD_MAP = {
    email: 'email',
    password: 'password',
    password_confirm: 'confirmPassword',
    first_name: 'firstName',
    last_name: 'lastName',
    role: 'role',
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (fieldErrors[e.target.name]) {
      setFieldErrors((prev) => ({ ...prev, [e.target.name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    if (formData.password !== formData.confirmPassword) {
      setFieldErrors({ confirmPassword: t('Passwords do not match') || 'Passwords do not match' });
      return;
    }

    setLoading(true);

    try {
      await authService.register({
        email: formData.email,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName,
        role: formData.role,
      });

      navigate('/login', { state: { message: t('Registration successful! Please login.') || 'Registration successful! Please login.' } });
    } catch (err) {
      const data = err.response?.data;

      if (data && typeof data === 'object' && !data.detail) {
        // Field-level validation errors, e.g. {"email": ["already exists"],
        // "password": ["too short"]} — show every one next to its field
        // instead of picking just one and hiding the rest.
        const nextFieldErrors = {};
        const genericMessages = [];
        Object.entries(data).forEach(([key, value]) => {
          const message = Array.isArray(value) ? value.join(' ') : value;
          const formKey = BACKEND_FIELD_MAP[key];
          if (formKey) {
            nextFieldErrors[formKey] = message;
          } else {
            genericMessages.push(message);
          }
        });
        setFieldErrors(nextFieldErrors);
        if (genericMessages.length) setError(genericMessages.join(' '));
        else if (Object.keys(nextFieldErrors).length === 0) setError(t('Registration failed. Please try again.'));
      } else {
        setError(data?.detail || t('Registration failed. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-surface-50">
      <BrandPanel />

      <div className="flex-1 flex items-center justify-center px-6 py-10 overflow-y-auto relative">
        <div
          className="absolute right-4 sm:right-6"
          style={{ top: 'max(1rem, calc(env(safe-area-inset-top) + 0.5rem))' }}
        >
          <LanguageSwitcher />
        </div>

        <div className="max-w-sm w-full">
          <Link to="/" className="mb-8 lg:hidden inline-block">
            <Logo size={36} textColor="text-gray-900" />
          </Link>

          <h2 className="text-2xl font-bold font-display tracking-tight text-gray-900">{t('Register')}</h2>
          <p className="text-sm text-gray-500 mt-1 mb-8">{t('Create an account as a student, teacher, or parent.')}</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-5 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('First Name')} required error={fieldErrors.firstName}>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={fieldErrors.firstName}
                  required
                />
              </Field>
              <Field label={t('Last Name')} required error={fieldErrors.lastName}>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={fieldErrors.lastName}
                  required
                />
              </Field>
            </div>

            <Field label={t('Email')} required error={fieldErrors.email}>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={fieldErrors.email}
                required
              />
            </Field>

            <Field label={t('Register as')} required error={fieldErrors.role}>
              <Select id="role" name="role" value={formData.role} onChange={handleChange}>
                <option value="student">{t('Student')}</option>
                <option value="teacher">{t('Teacher')}</option>
                <option value="parent">{t('Parent')}</option>
              </Select>
            </Field>

            <Field
              label={t('Password')}
              required
              error={fieldErrors.password}
              hint={t('At least 8 characters, not something too common or all numbers.')}
            >
              <Input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                error={fieldErrors.password}
                required
              />
            </Field>

            <Field label={t('Confirm Password')} required error={fieldErrors.confirmPassword}>
              <Input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={fieldErrors.confirmPassword}
                required
              />
            </Field>

            <Button type="submit" loading={loading} className="w-full" size="lg">
              {loading ? t('Registering...') : t('Register')}
            </Button>
          </form>

          <p className="text-center mt-6 text-sm text-gray-500">
            {t('Already have account')}{' '}
            <Link to="/login" className="text-primary-600 font-medium hover:underline">
              {t('Login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;
